export type AssessmentSectionKey = 'what' | 'why' | 'impact' | 'next';

export const ASSESSMENT_HEADING_MAP = new Map<string, AssessmentSectionKey>([
  ['what happened', 'what'],
  ['what', 'what'],
  ['why this is the leading diagnosis', 'why'],
  ['why reap thinks this', 'why'],
  ['why', 'why'],
  ['customer impact', 'impact'],
  ['impact', 'impact'],
  ['recommended next steps', 'next'],
  ['what to check next', 'next'],
  ['next steps', 'next'],
  ['how to investigate', 'next']
]);

export const ASSESSMENT_SECTION_TITLES: Record<AssessmentSectionKey, string> = {
  what: 'What happened',
  why: 'Why this happened',
  impact: 'Impact',
  next: 'Next steps'
};

export type AssessmentSection = {
  key: AssessmentSectionKey;
  heading: string;
  lines: string[];
};

export const parseAssessmentDescription = (text?: string | null): { intro: string[]; sections: AssessmentSection[] } => {
  if (!text) {
    return { intro: [], sections: [] };
  }

  const lines = normalizeAssessmentDescription(text).split(/\r?\n/);
  const intro: string[] = [];
  const sections: AssessmentSection[] = [];
  let currentSection: AssessmentSection | null = null;

  lines.forEach((line) => {
    const normalizedLine = line.trim();
    const labeled = parseAssessmentLabeledLine(normalizedLine);
    if (labeled) {
      if (labeled.key === 'skip') {
        return;
      }
      currentSection = sections.find((section) => section.key === labeled.key) ?? null;
      if (!currentSection) {
        currentSection = {
          key: labeled.key,
          heading: ASSESSMENT_SECTION_TITLES[labeled.key],
          lines: []
        };
        sections.push(currentSection);
      }
      if (labeled.content) {
        currentSection.lines.push(labeled.content);
      }
      return;
    }

    const key = ASSESSMENT_HEADING_MAP.get(normalizedLine.toLowerCase());
    if (key) {
      currentSection = {
        key,
        heading: ASSESSMENT_SECTION_TITLES[key],
        lines: []
      };
      sections.push(currentSection);
      return;
    }

    if (!normalizedLine) {
      return;
    }

    if (currentSection) {
      currentSection.lines.push(normalizedLine);
    } else {
      intro.push(normalizedLine);
    }
  });

  return { intro, sections };
};

export const normalizeAssessmentDescription = (text: string): string => {
  return text.replace(/\s+(Summary:|Subtype:|Likely Impact:|Why REAP thinks this:|What to check next:|Incident Detail:)/gi, '\n$1');
};

export const parseAssessmentLabeledLine = (
  line: string
): { key: AssessmentSectionKey | 'skip'; content: string } | null => {
  const match = line.match(/^(Summary|Subtype|Likely Impact|Why REAP thinks this|What to check next|Incident Detail):\s*(.*)$/i);
  if (!match) {
    return null;
  }

  const label = match[1].trim().toLowerCase();
  const content = match[2].trim();
  switch (label) {
    case 'summary':
      return { key: 'what', content };
    case 'likely impact':
      return { key: 'impact', content };
    case 'why reap thinks this':
      return { key: 'why', content };
    case 'what to check next':
      return { key: 'next', content };
    case 'incident detail':
      return { key: 'what', content };
    case 'subtype':
      return { key: 'skip', content: '' };
    default:
      return null;
  }
};

export const buildIncidentReferenceLine = (targetId?: string | null, displayId?: string | null): string => {
  const trimmedTargetId = (targetId ?? '').trim();
  const trimmedDisplayId = (displayId ?? '').trim();

  if (trimmedTargetId) {
    return `Ref: ${trimmedTargetId}`;
  }
  return trimmedDisplayId ? `Ref: ${trimmedDisplayId}` : '';
};

export const stripIncidentDescriptionMetadata = (description?: string | null): string => {
  const text = description?.trim();
  if (!text) {
    return '';
  }

  const metadataHeadings = new Set(['target', 'target id', 'target type']);
  const lines = text.split(/\r?\n/);
  const filtered: string[] = [];

  for (let index = 0; index < lines.length; index += 1) {
    const normalized = lines[index].trim();
    const heading = normalized.toLowerCase().replace(/:$/, '');
    if (!metadataHeadings.has(heading)) {
      filtered.push(lines[index]);
      continue;
    }

    let lookahead = index + 1;
    while (lookahead < lines.length && lines[lookahead].trim() === '') {
      lookahead += 1;
    }
    if (lookahead >= lines.length) {
      continue;
    }
    if (parseAssessmentLabeledLine(lines[lookahead].trim()) || ASSESSMENT_HEADING_MAP.has(lines[lookahead].trim().toLowerCase())) {
      continue;
    }

    index = lookahead;
    while (index + 1 < lines.length && lines[index + 1].trim() === '') {
      index += 1;
    }
  }

  return filtered.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};
